/**
 * Hindi message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: formal-polite आप (locked in l10n/locales.md);
 * imperatives take the -एँ / -िए form (भेजें, खोलें).
 *
 * Platform terms follow modern Google-hi UI conventions (थीम for theme,
 * बातचीत for conversation, पेयर करना for pair, हटाएँ for dismiss,
 * डिसकनेक्ट करें for disconnect); "Glance" is the untranslated feature
 * brand name (glossary D1) and "क्लिप" is the loanword for clip (D2,
 * rendered in Devanagari). "receiver" surface = रिसीवर.
 *
 * Plural notes: hi is a {one, other} CLDR locale whose `one` category
 * covers BOTH 0 and 1, so count-bearing bodies always interpolate the
 * count and never hardcode a number. Many Hindi count-nouns are invariant
 * across both forms (डिवाइस, संदेश, कॉल, वर्ण, मिलान); they still route
 * through `selectPlural("hi", …)` to keep the shape identical to en.
 * सूचना / सूचनाएँ genuinely inflects.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "कनेक्ट है",
    statusReconnecting: "फिर से कनेक्ट हो रहा है…",
    statusDisconnected: "कनेक्ट नहीं है",
    openSettings: "सेटिंग खोलें",
    pairedFallbackDevice: "आपका फ़ोन",
    publicModeToggleEnable: "सूचना सामग्री छिपाएँ",
    publicModeToggleDisable: "सूचना सामग्री दिखाएँ",
  },

  notifications: {
    replyAriaLabel: "जवाब दें",
    replyPlaceholder: "जवाब…",
    replySend: "भेजें",
    sent: "भेज दिया",
    failedToSend: "भेजने में विफल",
    unread: "अपठित",
    dismiss: "हटाएँ",
    notificationImageAlt: "सूचना इमेज",
  },

  conversations: {
    replyAriaLabel: "जवाब दें",
    replyPlaceholder: "जवाब…",
    replySend: "भेजें",
    sent: "भेज दिया",
    failedToSend: "भेजने में विफल",
    dismiss: "बातचीत हटाएँ",
    messagesListLabel: "इस बातचीत के संदेश",
    deviceOwner: "आप",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "कोई अपठित संदेश नहीं"
        : `${count} ${selectPlural("hi", count, {
            one: "अपठित संदेश",
            other: "अपठित संदेश",
          })}`,
  },

  list: {
    today: "आज",
    yesterday: "कल",
    searchNotifications: "सूचनाएँ खोजें",
    clearSearch: "खोज हटाएँ",
    clearAll: "सभी हटाएँ",
    markAllRead: "सभी को पढ़ा हुआ मार्क करें",
    groupCheckboxLabel: "ग्रुप",
    noNotificationsTitle: "अभी तक कोई सूचना नहीं",
    noNotificationsDesc: "आपके फ़ोन से मिरर हुए नए संदेश यहाँ दिखेंगे.",
    noMatchesTitle: "कोई मेल नहीं",
    notificationsList: "सूचनाएँ",
    privacyAppList: "ऐप के हिसाब से सूचनाएँ",
    clear: "हटाएँ",
    // "मिलान" is invariant across Hindi plural forms; count still routes
    // through selectPlural to keep the shape identical to en.
    clearMatches: (count: number): string =>
      `${count} ${selectPlural("hi", count, { one: "मिलान", other: "मिलान" })} हटाएँ`,
    noMatchesDesc: (query: string): string => `“${query}” से कोई सूचना मेल नहीं खाती.`,
    appNotificationsGroup: (appName: string): string => `${appName} सूचनाएँ`,
    dismissAllFrom: (appName: string): string => `${appName} की सभी हटाएँ`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("hi", count, { one: "संदेश", other: "संदेश" })
          : kind === "calls"
            ? selectPlural("hi", count, { one: "कॉल", other: "कॉल" })
            : selectPlural("hi", count, { one: "सूचना", other: "सूचनाएँ" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "हटाएँ",
    kbdNavigate: "नेविगेट करें",
    kbdOrSeparator: "या",
  },

  settings: {
    back: "सूचनाओं पर वापस",
    settings: "सेटिंग",
    appearance: "दिखावट",
    theme: "थीम",
    themeAuto: "ऑटो",
    themeLight: "लाइट",
    themeDark: "डार्क",
    language: "भाषा",
    languageSystemDefault: "सिस्टम डिफ़ॉल्ट",
    notificationsSection: "सूचनाएँ",
    osNotifications: "OS सूचनाएँ",
    callNotifications: "कॉल सूचनाएँ",
    privacySection: "गोपनीयता",
    analyticsToggle: "अनाम उपयोग आँकड़े",
    analyticsToggleDescription: "सिर्फ़ गिनती और मील के पत्थर — आपकी सूचनाएँ कभी नहीं.",
    connection: "कनेक्शन",
    status: "स्थिति",
    statusConnected: "कनेक्ट है",
    statusReconnecting: "फिर से कनेक्ट हो रहा है…",
    statusDisconnected: "कनेक्ट नहीं है",
    disconnectFromPhone: "फ़ोन से डिसकनेक्ट करें",
    disconnectConfirm: "क्या आप पक्का करना चाहते हैं? इससे आपके डिवाइस की पेयरिंग हट जाएगी.",
    yesDisconnect: "हाँ, डिसकनेक्ट करें",
    cancel: "रद्द करें",
    aboutAndSupport: "जानकारी और सहायता",
    version: "वर्शन",
    encryption: "एन्क्रिप्शन",
    encryptionValue: "E2E (NaCl)",
    needHelp: "मदद चाहिए? __EMAIL__ पर ईमेल करें",
    disconnectFailed: "डिसकनेक्ट नहीं हो सका — कृपया फिर से कोशिश करें",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `डिसकनेक्ट हो गया, पर लोकल क्लीनअप विफल: ${errorMsg}. कृपया पेज फिर से लोड करें.`,
    disconnectLocalCleanupFailed: "डिसकनेक्ट हो गया, पर लोकल क्लीनअप विफल. कृपया पेज फिर से लोड करें.",
  },

  pairing: {
    enterCodeInstruction: "यह कोड Reflecto ऐप में डालें",
    pairedSuccessfully: "पेयरिंग सफल!",
    pairingFailed: "पेयरिंग विफल",
    requestingPairingCode: "पेयरिंग कोड मांगा जा रहा है…",
    codeExpired: "कोड खत्म हो गया",
    tryAgain: "फिर से कोशिश करें",
    failedToStartPairing: "पेयरिंग शुरू नहीं हो सकी",
    needAppText: "Android ऐप चाहिए? ऐक्सेस के लिए __EMAIL__ पर ईमेल करें.",
  },

  dnd: {
    title: "परेशान न करें",
    description: "इस डिवाइस पर अगले एक घंटे के लिए सूचनाएँ रोकें.",
    snooze1h: "1 घंटे के लिए रोकें",
    snoozeActiveUntil: (formattedTime: string): string => `${formattedTime} तक चालू`,
    cancelSnooze: "रद्द करें",
  },

  connectors: {
    title: "कनेक्ट की गई सेवाएँ",
    emptyBody:
      "अभी तक कोई कनेक्ट की गई सेवा नहीं. ChatGPT या Claude को उनकी कनेक्टर सेटिंग से कनेक्ट करें, फिर अपने फ़ोन पर कोड की पुष्टि करें.",
    scopeSend: "सूचनाएँ भेज सकता है",
    added: (date: string): string => `जोड़ा गया ${date}`,
    lastUsed: (relative: string): string => `आखिरी बार इस्तेमाल ${relative}`,
    neverUsed: "कभी इस्तेमाल नहीं हुआ",
    revoke: "डिसकनेक्ट करें",
    revokeConfirm: (label: string): string => `${label} को डिसकनेक्ट करें? इसका ऐक्सेस तुरंत खत्म हो जाएगा.`,
    revokeAction: "डिसकनेक्ट करें",
    revokeConfirmLabel: (label: string): string => `${label} को डिसकनेक्ट करें`,
    cancel: "रद्द करें",
    loadError: "कनेक्ट की गई सेवाएँ लोड नहीं हो सकीं. कृपया फिर से कोशिश करें.",
    revokeError: "सेवा डिसकनेक्ट नहीं हो सकी. कृपया फिर से कोशिश करें.",
    retry: "फिर से कोशिश करें",
  },

  feedback: {
    title: "फ़ीडबैक भेजें",
    close: "बंद करें",
    messageLabel: "आपका संदेश",
    messagePlaceholder: "कोई बग मिला, कोई आइडिया है, या कुछ ठीक नहीं लग रहा? हमें बताएँ.",
    categoryLabel: "श्रेणी",
    categoryBug: "बग",
    categoryIdea: "आइडिया",
    categoryOther: "अन्य",
    emailLabel: "ईमेल (वैकल्पिक, सिर्फ़ तभी जब आप जवाब चाहें)",
    emailPlaceholder: "you@example.com",
    emailHelp: "हम आपको किसी सूची में नहीं जोड़ेंगे.",
    disclosure:
      "आपका संदेश Reflecto के सर्वर पर भेजा जाता है और हमारे इंटरनल इशू ट्रैकर में सेव किया जाता है. रिपोर्ट को आपस में जोड़ने के लिए हम आपके ऐप वर्शन और पेयरिंग के दौरान दी गई एक डिवाइस ID जोड़ते हैं. हम आपकी सूचनाएँ, संपर्क या कोई निजी डेटा नहीं देखते. ईमेल वैकल्पिक है — इसे सिर्फ़ तभी दें जब आप जवाब चाहें. हम आपको किसी सूची में नहीं जोड़ते.",
    submit: "फ़ीडबैक भेजें",
    sending: "भेजा जा रहा है…",
    success: "धन्यवाद — इसे कोई व्यक्ति पढ़ता है, आम तौर पर 24 घंटे के भीतर",
    error: "भेजा नहीं जा सका. कृपया फिर से कोशिश करें.",
    done: "हो गया",
    sendFeedback: "फ़ीडबैक भेजें",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "बंद करें",
    aiSummaryTag: "AI सारांश",
    errorAi: "अभी सारांश नहीं बन सका.",
    retryCta: "फिर से कोशिश करें",
    downloadHeading: "AI सारांश पाएँ",
    downloadBody:
      "आपने जो छूटा, उसे डिवाइस पर ही चलने वाले AI रीकैप से जानें. यह पूरी तरह आपके डिवाइस पर चलता है.",
    downloadCta: "मॉडल डाउनलोड करें",
    downloadPrivacyNote: "एक बार का डाउनलोड. सर्वर आपकी सूचनाएँ कभी नहीं देखता.",
    downloadingHeading: "मॉडल डाउनलोड हो रहा है…",
    caughtUp: "आप पूरी तरह अप-टू-डेट हैं — अभी किसी चीज़ को आपके ध्यान की ज़रूरत नहीं.",
    needsYou: "आपका ध्यान चाहिए",
    quiet: "अन्य",
    scanning: (pending: number): string => `${pending} का सारांश बन रहा है…`,
    jump: "सूचना खोलें",
    affordanceReply: "जवाब दें",
    affordanceOpen: "खोलें",
    affordanceCopyOtp: "कोड कॉपी करें",
    buttonLabel: "Glance",
  },

  update: {
    title: "अपडेट उपलब्ध",
    description: "Reflecto का नया वर्शन तैयार है. अपडेट करने के लिए रीलोड करें.",
    reload: "रीलोड करें",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `हमने इस डिवाइस का नाम “${name}” रखा.`,
    cardLooksGood: "ठीक है",
    cardEdit: "बदलें",
    cardAriaLabel: "डिवाइस का नाम पक्का करें",
    renameTitle: "इस डिवाइस का नाम बदलें",
    renameLabel: "डिवाइस का नाम",
    renameSave: "सेव करें",
    renameCancel: "रद्द करें",
    renameFailed: "नाम नहीं बदल सका. कृपया फिर से कोशिश करें.",
  },

  collision: {
    title: (name: string): string => `आपके पास पहले से एक “${name}” है`,
    subtitle: "इसे हम क्या नाम दें?",
    chipWork: (name: string): string => `वर्क ${name}`,
    chipHome: (name: string): string => `होम ${name}`,
    chipPersonal: (name: string): string => `निजी ${name}`,
    customLabel: "कस्टम नाम",
    save: "सेव करें",
    useAnyway: "फिर भी मूल नाम इस्तेमाल करें",
    saveFailed: "नया नाम सेव नहीं हो सका. कृपया फिर से कोशिश करें.",
    ariaLabel: "डुप्लिकेट डिवाइस नाम सुलझाएँ",
  },

  clipboard: {
    title: "क्लिपबोर्ड",
    empty: "आपके दूसरे डिवाइस के क्लिप यहाँ दिखते हैं. कॉपी करने के लिए टैप करें.",
    from: (label: string): string => `${label} से`,
    copy: "क्लिपबोर्ड पर कॉपी करें",
    copied: "कॉपी हो गया",
    reveal: "दिखाएँ",
    hide: "छिपाएँ",
    maskedHint: "सामग्री छिपी है — इसे देखने के लिए दिखाएँ दबाएँ",
    sensitiveChip: "संवेदनशील",
    cloudChip: "आपके API टोकन से",
    sendPlaceholder: "भेजने के लिए टेक्स्ट पेस्ट करें…",
    sendCta: "भेजें",
    sendSensitiveToggle: "संवेदनशील मार्क करें",
    sendSuccess: (n: number): string =>
      `${n} ${selectPlural("hi", n, { one: "डिवाइस", other: "डिवाइस" })} पर भेजा गया.`,
    sendPartial: (n: number, total: number): string =>
      `${total} में से ${n} ${selectPlural("hi", total, { one: "डिवाइस", other: "डिवाइस" })} तक पहुँचा.`,
    sendNoDevices: "भेजने के लिए कोई पेयर किया गया डिवाइस नहीं.",
    sendFailed: "भेजा नहीं जा सका. कृपया फिर से कोशिश करें.",
    sendEmpty: "भेजने के लिए कुछ नहीं.",
    dockExpand: "क्लिपबोर्ड इतिहास दिखाएँ",
    dockCollapse: "छोटा करें",
    dockSendTitle: "अपने डिवाइस पर भेजें",
    dockFromClipboard: (chars: number): string =>
      `क्लिपबोर्ड से · ${chars} ${selectPlural("hi", chars, {
        one: "वर्ण",
        other: "वर्ण",
      })}`,
    dockSend: (n: number): string => `${n} को भेजें`,
    dockCancel: "रद्द करें",
    dockRecentHeading: "हाल के",
    dockEmptyClipboard: "आपका क्लिपबोर्ड खाली है.",
    shareTitle: "अपने डिवाइस पर भेजें",
    shareReview:
      "आप जो भेज रहे हैं उसकी जाँच कर लें. यह एंड-टू-एंड एन्क्रिप्टेड होकर आपके पेयर किए गए डिवाइस पर जाता है.",
    shareSend: "भेजें",
    shareCancel: "रद्द करें",
    shareNotPaired: "शेयर किया गया टेक्स्ट भेजने के लिए पहले कोई डिवाइस पेयर करें.",
    shareOpenApp: "Reflecto खोलें",
  },
  install: {
    title: "Reflecto इंस्टॉल करें",
    body: "तेज़ ऐक्सेस के लिए इसे अपनी होम स्क्रीन पर जोड़ें.",
    cta: "इंस्टॉल करें",
    dismissAria: "इंस्टॉल प्रॉम्प्ट खारिज करें",
  },
  common: {
    loading: "लोड हो रहा है",
  },
  errors: {
    notPaired: "पेयर नहीं है",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${os} पर ${browser}`,
    webFallback: "वेब ब्राउज़र",
  },

  palette: {
    placeholder: "कमांड लिखें…",
    noResults: "कोई मिलती-जुलती कमांड नहीं",
    ariaLabel: "कमांड पैलेट",
    clearAll: "सभी सूचनाएँ हटाएँ",
    glance: "Glance सारांश",
    cancelSnooze: "रोक रद्द करें",
    copyClip: "अंतिम प्राप्त क्लिप कॉपी करें",
  },
} satisfies MessagesShape;
